import { flatten, path, defaultTo } from 'ramda';
import { v4 as uuidv4 } from 'uuid';

import {
  StopTypes,
  StopProgress,
  StopServices,
  StopExceptions,
  DriverRequests,
} from '../enums/FilterTypes';
import stopTypes, { originalTypes, pickupKindTypes } from '../enums/stopTypes';
import { isAddCollectionRequest, isAddParcelRequest } from './request';
import { joinArrayToString } from '../utils/string';
import { LUNCH_BREAK } from '../constants/strings';
import { DELIVERY_STOP_TYPES } from '../components/modals/SelectByCriteria/constants';
import {
  ignoreServiceException,
  isBulk,
  isPerishable,
  isPremium,
  isServiceException,
  isVIP,
} from './routeDetail';
import { createTimeRange, getDiffInMinutes } from '../utils/date';
import {
  getPickupKind,
  parcelTableDataMapper,
} from '../features/routeDetail/model';

export const FilterTypes = {
  stopType: 'stopType',
  stopProgress: 'stopProgress',
  service: 'service',
  exception: 'exception',
  request: 'request',
  pickupKind: 'pickupKind',
};

export const formatStopAddress = (stop) => {
  if (isBreakStop(stop)) {
    return LUNCH_BREAK.toUpperCase();
  }
  return joinArrayToString([
    stop.stopAddress.contactName,
    stop.stopAddress.address1,
  ]);
};

const formatRouteStopLabel = (routeStop) => {
  const address = formatStopAddress(routeStop);

  return joinArrayToString(
    [routeStop.displayNumber, routeStop.stopAddress?.postcode, address],
    ' - '
  );
};

export const createStopOptions = (stops) =>
  stops.map((routeStop) => ({
    value: routeStop.stopNumber,
    label: formatRouteStopLabel(routeStop),
  }));

export const isBreakStop = (stop) =>
  stop.stopTypeDescription === stopTypes.break;

export const isCollectionStop = (stop) =>
  stop.stopTypeDescription === stopTypes.collection;

export const isShopStop = (stop) => stop.stopTypeDescription === stopTypes.shop;

export const getStopPickupKind = (stop) =>
  getPickupKind({
    parcel: isCollectionStop(stop) ? [stop.collection] : stop.parcel,
  }) || '';

export const isStopPickupKindLocker = (stop) =>
  getStopPickupKind(stop) === pickupKindTypes.locker;

export const isStopPickupKindShop = (stop) =>
  getStopPickupKind(stop) === pickupKindTypes.shop;

export const findKingParcel = (parcels = []) =>
  parcels?.find((p) => +p.parcelIndex === 1);

export const findKingParcelPostcode = (stop) =>
  findKingParcel(stop?.parcel)?.address?.postcode;

export const getRemainingParcelCodes = (stops, selectedParcels) =>
  (stops?.length &&
    getStopParcelCodesWithRouteDate(stops[0]).filter(
      ({ parcelCode }) => !selectedParcels[parcelCode]
    )) ||
  [];

export const getRemainingParcelsFromStop = (stops, selectedParcels) =>
  (stops?.length &&
    getRemainingParcelsWithConsignmentValue(stops[0]).filter(
      ({ parcelCode }) => !selectedParcels[parcelCode]
    )) ||
  [];

export const getSelectedParcelsCount = (parcels) => Object.keys(parcels).length;

export const generateNewStop = (stopTemplate, stops, requests) => {
  const stopNumber = getHighestStopNumber(stops, requests) + 1;
  const sequenceNumber = getHighestSequenceNumber(stops) + 1;

  return {
    ...stopTemplate,
    stopNumber,
    sequenceNumber,
    displayNumber: `${sequenceNumber}`,
  };
};

export const createCollectionStop = ({
  collectionCode,
  collectionType,
  collectionReference,
  serviceStartTime,
  serviceEndTime,
  address,
}) => ({
  ...originalTypes.collection,
  bulk: false,
  consolidated: false,
  collectionIndex: 1,
  eta: '',
  opportunityTime: 0,
  exception: 0,
  collectionCode,
  collectionNumber: collectionReference,
  collectionType,
  serviceStartTime,
  serviceEndTime,
  stopAddress: address,
});

export const makeStopForRequest = (request) => {
  if (isAddParcelRequest(request)) {
    return { ...originalTypes.delivery, parcel: [] };
  }

  if (isAddCollectionRequest(request)) {
    return { ...originalTypes.collection, collection: {} };
  }

  return {};
};

export const getStopParcelCodes = ({ parcel = [] }) =>
  parcel.reduce(
    (acc, item) =>
      !!item && item.parcelCode ? [...acc, item.parcelCode] : acc,
    []
  );

export const getStopParcelCodesWithRouteDate = ({ parcel = [] }) =>
  parcel.reduce(
    (acc, item) =>
      !!item && item.parcelCode
        ? [
            ...acc,
            {
              parcelCode: item.parcelCode,
              routeDate: item.routeDate,
            },
          ]
        : acc,
    []
  );

export const getRemainingParcelsWithConsignmentValue = (stop) =>
  parcelTableDataMapper(stop.parcel, stop).reduce(
    (acc, item) =>
      !!item && item.parcelCode
        ? [
            ...acc,
            {
              ...item,
              consignmentValue: item.consignmentNumber
                ? item.consignmentNumber + item.shipmentDate
                : null,
            },
          ]
        : acc,
    []
  );

export const getRouteDateForParcelFromRequest = (request, stops, routeDate) => {
  const parcel = stops
    .find(({ stopNumber }) => request.stopNumber === stopNumber)
    ?.parcel.find(({ parcelCode }) => request.parcelCode === parcelCode);

  return parcel?.routeDate || routeDate;
};

export const getMergeToParcelList = (selectedStops, pickedStop) => [
  ...new Set(
    flatten([
      selectedStops.map(getStopParcelCodesWithRouteDate),
      getStopParcelCodesWithRouteDate(pickedStop),
    ])
  ),
];

export const findStopByStopNumber = (stops, stopNumberToSearch) =>
  stops.find(({ stopNumber }) => stopNumber === stopNumberToSearch);

export const sortByStopNumber = (stops) =>
  stops.sort((a, b) => a.stopNumber - b.stopNumber);

export const getHighestStopNumber = (stops = [], requests = []) =>
  Math.max(
    0,
    ...stops.map(path(['stopNumber'])),
    ...requests.map(path(['stopNumber']))
  );

export const getHighestSequenceNumber = (stops = [], requests = []) =>
  Math.max(
    1,
    ...stops.map(path(['stopNumber'])),
    ...requests.map(path(['stopNumber']))
  );

export const getServiceWindow = ({
  displayServiceWindow,
  serviceStartTime,
  serviceEndTime,
}) =>
  displayServiceWindow ? createTimeRange(serviceStartTime, serviceEndTime) : '';

export const formatDisplayNumber = (displayNumber) =>
  `${displayNumber}`.replace('.', '-');

export const formatDisplayNumberToFloat = (stop) => ({
  ...stop,
  displayNumber: `${stop.displayNumber}`.replace('-', '.'),
});

export const getStopByStopNumber = (stopNumber) => (stop) =>
  stopNumber === stop.stopNumber;

export const createNewStop = ({ stops, request }) => {
  const stopNumber = getHighestStopNumber(stops) + 1;
  const sequenceNumber = getHighestSequenceNumber(stops) + 1;

  return {
    ...createCollectionStop(request),
    stopId: uuidv4(),
    stopNumber,
    sequenceNumber,
    displayNumber: `${sequenceNumber}`,
  };
};

export const getStopAndStopIndexByStopNumber = (stops, stopNumber) =>
  stops.findIndex(getStopByStopNumber(stopNumber));

export const filterByStopNumber = (stops, stopNumber) =>
  stops.filter((stop) => stop.stopNumber !== stopNumber);

export const getProcessRequestUpdatedStops = ({
  stops,
  newStop,
  removeStopNumber,
}) => {
  if (newStop) {
    return stops.concat(newStop);
  }

  if (removeStopNumber) {
    return filterByStopNumber(stops, removeStopNumber);
  }

  return stops;
};

export const calculateDeliveryStopType = (stop) => {
  if (stop.stopTypeDescription === stopTypes.precise) {
    return DELIVERY_STOP_TYPES.PRECISE;
  }
  if (isPremium(stop)) {
    return DELIVERY_STOP_TYPES.PREMIUM;
  }
  if (isBulk(stop)) {
    return DELIVERY_STOP_TYPES.BULK;
  }
  if (isPerishable(stop)) {
    return DELIVERY_STOP_TYPES.PERISHABLE;
  }
  if (stop.stopTypeDescription === stopTypes.shop) {
    return DELIVERY_STOP_TYPES.PICKUP;
  }
  if (isVIP(stop)) {
    return DELIVERY_STOP_TYPES.VIP;
  }
  return DELIVERY_STOP_TYPES.STANDARD;
};

const includesStopType = (filters, stop) =>
  filters.some((type) => {
    switch (type) {
      case StopTypes.delivery:
        return stop.stopTypeDescription === stopTypes.delivery;
      case StopTypes.collections:
        return stop.stopTypeDescription === stopTypes.collection;
      case StopTypes.timed:
        return stop.stopTypeDescription === stopTypes.timed;
      case StopTypes.pickup:
        return stop.stopTypeDescription === stopTypes.shop;
      case StopTypes.perishable:
        return !!stop.perishable;
      case StopTypes.vip:
        return !!stop.vipDelivery;
      case StopTypes.bulk:
        return !!stop.bulk;
      case StopTypes.premium:
        return !!stop.premium;
      case StopTypes.swapit:
        return !!stop.swapit;
      case StopTypes.smalls:
        return stop.totalSmalls > 0;
      default:
        return false;
    }
  });

const includesProgress = (filters, stop) =>
  filters.some((type) => {
    switch (type) {
      case StopProgress.nonCAD:
        return stop.totalCadParcels < stop.totalParcels;
      case StopProgress.CAD:
        return stop.totalCadParcels === stop.totalParcels;
      case StopProgress.nonLoaded:
        return stop.totalLoaded < stop.totalParcels;
      case StopProgress.loaded:
        return stop.totalLoaded === stop.totalParcels;
      default:
        return false;
    }
  });

const includesService = (filters, stop) =>
  filters.some((type) => {
    switch (type) {
      case StopServices.tenThirty:
        return (
          stop.premium && getDiffInMinutes(stop.serviceEndTime, '12:00') < 0
        );
      case StopServices.twelve:
        return (
          stop.premium &&
          getDiffInMinutes(stop.serviceEndTime, '12:00') >= 0 &&
          getDiffInMinutes(stop.serviceEndTime, '13:00') <= 0
        );
      case StopServices.saturday:
        return !!stop.parcel.some((parcel) => parcel.saturdayService);
      case StopServices.sunday:
        return !!stop.parcel.some((parcel) => parcel.sundayService);
      case StopServices.afternoon:
        return (
          getDiffInMinutes(stop.serviceStartTime, '12:00') >= 0 &&
          getDiffInMinutes(stop.serviceStartTime, '17:00') < 0
        );
      case StopServices.evening:
        return getDiffInMinutes(stop.serviceStartTime, '17:00') >= 0;
      case StopServices.precise:
        return (
          getDiffInMinutes(stop.serviceEndTime, stop.serviceStartTime) === 60
        );
      default:
        return false;
    }
  });

const includesException = (filters, stop) =>
  filters.some((type) => {
    switch (type) {
      case StopExceptions.none:
        return (
          !stop.exception ||
          (isServiceException(stop) && ignoreServiceException(stop))
        );
      case StopExceptions.postcode:
        return stop.exception === StopExceptions.postcode;
      case StopExceptions.service:
        return (
          stop.exception === StopExceptions.service && !stop.ignoreException
        );
      case StopExceptions.duty:
        return stop.exception === StopExceptions.duty;
      case StopExceptions.opportunity:
        return stop.exception === StopExceptions.opportunity;
      case StopExceptions.range:
        return stop.exception === StopExceptions.range;
      case StopExceptions.legalDuty:
        return stop.exception === StopExceptions.legalDuty;
      default:
        return false;
    }
  });
const includesRequests = (filters, stop) =>
  filters.some((type) => {
    if (type === DriverRequests.Requests) {
      return !!stop.requests.length;
    }
    return false;
  });

export const filterStops = (filters, stops = []) =>
  stops.filter((stop) => {
    if (
      filters[FilterTypes.stopType] &&
      !includesStopType(filters[FilterTypes.stopType], stop)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.stopProgress] &&
      !includesProgress(filters[FilterTypes.stopProgress], stop)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.service] &&
      !includesService(filters[FilterTypes.service], stop)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.exception] &&
      !includesException(filters[FilterTypes.exception], stop)
    ) {
      return false;
    }

    if (
      filters[FilterTypes.pickupKind] &&
      !filters[FilterTypes.pickupKind].some(
        (type) => type === getStopPickupKind(stop)
      )
    ) {
      return false;
    }

    return !(
      filters[FilterTypes.request] &&
      !includesRequests(filters[FilterTypes.request], stop)
    );
  });

export const uncheckBulkStops = (stops, bulkStopThreshold) =>
  stops.map((stop) => ({
    ...stop,
    bulk: stop.parcel.length < bulkStopThreshold ? false : stop.bulk,
  }));

export const getManifestValue = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return '';
  }
  if (locations.length === 1) {
    return locations[0];
  }

  const sorted = [...locations].sort();
  const [first, second] = sorted;
  return sorted.length > 2 ? `${first}, ${second}...` : `${first}, ${second}`;
};

export const getManifestLocations = (stop) => {
  const manifestLocations = new Set(
    stop.parcel.map((parcel) => parcel.manifestLocation).filter(Boolean)
  );
  return [...manifestLocations].sort();
};

export const createStopInfo = ({ stop, parcelData }) => ({
  stopId: stop.stopId,
  displayNumber: stop.displayNumber,
  parcelsInfo: parcelData.reduce((acc, parcel) => {
    const parcelNum = parcel.parcelNumber;
    const data = {
      vip: defaultTo('', parcel.vipDelivery),
      perishable: defaultTo('', parcel.perishable),
      pharma: defaultTo('', parcel.pharma),
      highValue: defaultTo('', parcel.highValue),
      swapit: defaultTo('', parcel.swapit),
      type: defaultTo('', parcel.type),
      udprn: parcel.udprnSuffix
        ? `${defaultTo('', parcel.udprn)}-${defaultTo('', parcel.udprnSuffix)}`
        : defaultTo('', parcel.udprn),
      reloved: defaultTo('', parcel.reloved),
      outbound: defaultTo('', parcel.outbound),
      propertyType: defaultTo('', parcel.propertyType),
      idRequired: defaultTo('', parcel.requiredIdType),
    };

    Object.entries(data).forEach(([key, value]) => {
      if (!acc[key]) {
        acc[key] = {};
      }
      acc[key][parcelNum] = value;
    });

    return acc;
  }, {}),
  parcels: parcelData.reduce((acc, parcel) => {
    acc[parcel.parcelNumber] = {
      vip: parcel.vipDelivery,
      perishable: parcel.perishable,
      pharma: parcel.pharma,
      highValue: parcel.highValue,
      type: parcel.type,
      udprn: parcel.udprnSuffix
        ? `${parcel.udprn} + ${parcel.udprnSuffix}`
        : `${parcel.udprn}`,
      reloved: parcel.reloved,
      outbound: parcel.outbound,
      propertyType: parcel.propertyType,
      idRequired: parcel.requiredIdType,
    };
    return acc;
  }, {}),
});
